import { Check } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button.js";

export interface OnboardingStepperProps {
  steps: readonly string[];
  /** Index of the open step. */
  current: number;
  /** Index of the step the user just saved; plays the completion animation once. */
  justCompleted?: number | null;
  /** Called when a finished step is clicked, to go back to it. */
  onSelect?: (index: number) => void;
  className?: string;
}

// Padding shared by the clickable (finished) node and the plain ones, so the
// row doesn't shift when a step flips from active to done.
const NODE_SPACING = "gap-2 p-1 pr-2";

/**
 * Horizontal stepper for the onboarding bar. Finished steps are green ticks you
 * can click to revisit, the open step is a filled brand circle with its label,
 * upcoming steps are numbered. Below 2xl only the open step keeps a visible
 * label, so eight steps still fit beside the title in one row.
 *
 * Completion animation (keyed off `justCompleted`, never on first load): the
 * circle pops, a ripple leaves it, the tick draws itself, then the connector
 * to the next step fills. Keyframes live in tokens.css.
 */
export function OnboardingStepper({ steps, current, justCompleted = null, onSelect, className }: OnboardingStepperProps) {
  return (
    <nav aria-label="Onboarding progress" className={cn("min-w-0 flex-1", className)}>
      <ol className="no-scrollbar flex items-center overflow-x-auto">
        {steps.map((label, i) => {
          const done = i < current;
          const active = i === current;
          const celebrate = done && i === justCompleted;
          const isLast = i === steps.length - 1;

          const node = (
            <>
              <span
                className={cn(
                  "relative flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors duration-300",
                  done && "bg-success text-white",
                  active && "bg-brand-600 text-white ring-4 ring-brand-600/20",
                  !done && !active && "bg-surface-100 text-surface-700 ring-1 ring-inset ring-surface-200",
                  celebrate && "stepper-complete"
                )}
              >
                {done ? <Check size={14} strokeWidth={3} className={cn(celebrate && "stepper-check-draw")} /> : i + 1}
                {celebrate && <span aria-hidden className="stepper-ripple pointer-events-none absolute inset-0 rounded-full bg-success" />}
                {/* Keyed by step so it replays each time a new step opens. */}
                {active && <span key={current} aria-hidden className="stepper-arrive pointer-events-none absolute inset-0 rounded-full border-2 border-brand-500" />}
              </span>
              <span
                className={cn(
                  "whitespace-nowrap text-xs",
                  active
                    ? "font-bold text-brand-700"
                    : cn("sr-only 2xl:not-sr-only font-medium", done ? "text-surface-700" : "text-surface-600")
                )}
              >
                {label}
              </span>
            </>
          );

          return (
            <li key={label} className={cn("flex items-center", !isLast && "flex-1")} {...(active ? { "aria-current": "step" as const } : {})}>
              {done ? (
                <Button
                  variant="ghost"
                  aria-label={`Go back to ${label}`}
                  onClick={() => onSelect?.(i)}
                  className={cn("h-auto shrink-0 rounded-full", NODE_SPACING)}
                >
                  {node}
                </Button>
              ) : (
                <span title={label} className={cn("flex shrink-0 items-center", NODE_SPACING)}>
                  {node}
                </span>
              )}
              {!isLast && (
                <span aria-hidden className="relative mx-1 h-0.5 min-w-4 flex-1 overflow-hidden rounded-full bg-surface-200">
                  <span
                    className={cn(
                      "absolute inset-0 origin-left rounded-full bg-success transition-transform duration-500 ease-out",
                      done ? "scale-x-100" : "scale-x-0",
                      // Let the circle pop first, then run the line to the next step.
                      celebrate && "delay-300"
                    )}
                  />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
